/**
 * Watering happens in *sessions*, not calendar days. A session is one pass of
 * the watering window; for a normal window (start < end) it lives inside a
 * single local day, but for a window that WRAPS midnight (e.g. 22:00–06:00) it
 * opens on one date and closes on the next.
 *
 * Both the planner and the executor must agree on two things at any instant:
 * which session we're acting on (its `sessionDate` = the local date the window
 * OPENS), and whether watering is live right now (`open`). Deriving that in one
 * place — the way `runActivity` centralizes "is water moving?" — keeps the two
 * schedulers from disagreeing across the midnight boundary.
 *
 * The `sessionDate` is always the CURRENT or NEXT-UPCOMING session:
 * - Before/inside today's window  → today's session.
 * - After a normal window closed  → tomorrow's session (plan the next one).
 * - Inside the early-morning tail of a wrapping window → yesterday's session
 *   (it opened last night and is still running).
 *
 * So a run is stamped with the date its window opened, and a run whose
 * `sessionDate` is earlier than the current one belongs to a session that has
 * already closed.
 */
import { hhmmToMinutes } from './capacity.js';
/** Whether a window wraps past midnight (closes at/after it opens the next day). */
export function windowWrapsMidnight(window) {
    return hhmmToMinutes(window.latestEnd) <= hhmmToMinutes(window.earliestStart);
}
/** Minutes-since-midnight → "HH:MM", clamped into a single day [00:00, 23:59]. */
export function minutesToHhmm(minuteOfDay) {
    const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minuteOfDay)));
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
/**
 * The concrete close time for a window on a day with the given local sunrise.
 * A FIXED end (or a SUNRISE end with no sunrise available) returns the
 * configured `latestEnd` unchanged; a SUNRISE end returns that sunrise shifted
 * by `sunriseOffsetMinutes` (negative = before sunrise), clamped into the day.
 */
export function resolveWindowEnd(window, sunriseLocal) {
    if (window.endMode !== 'SUNRISE' || !sunriseLocal)
        return window.latestEnd;
    return minutesToHhmm(hhmmToMinutes(sunriseLocal) + (window.sunriseOffsetMinutes ?? 0));
}
/**
 * The window with its `latestEnd` resolved to a concrete clock time for the
 * day's sunrise (see {@link resolveWindowEnd}). Returns the same object when
 * nothing changes, so callers can cheaply detect a no-op.
 */
export function resolveWindow(window, sunriseLocal) {
    const latestEnd = resolveWindowEnd(window, sunriseLocal);
    return latestEnd === window.latestEnd ? window : { ...window, latestEnd };
}
/** Shift a YYYY-MM-DD date string by whole days (UTC math; pure date arithmetic). */
export function shiftDate(isoDate, days) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/**
 * Minutes from `minuteOfDay` until the open window's `latestEnd`. For a window
 * that wraps midnight this crosses the day boundary: in the evening leg the
 * close is the next morning, in the early-morning leg it is later today. Only
 * meaningful while the window is open (see {@link sessionPhase}); a same-day
 * window just returns `end − now`.
 */
export function minutesUntilWindowClose(minuteOfDay, window) {
    const startMin = hhmmToMinutes(window.earliestStart);
    const endMin = hhmmToMinutes(window.latestEnd);
    if (windowWrapsMidnight(window) && minuteOfDay >= startMin) {
        // Evening leg: the window closes at `end` the following day.
        return 24 * 60 - minuteOfDay + endMin;
    }
    return endMin - minuteOfDay;
}
/**
 * Minutes from `minuteOfDay` until the window next OPENS. Only meaningful while
 * the window is closed (see {@link sessionPhase}) — the mirror of
 * {@link minutesUntilWindowClose}. Crosses the day boundary when the open is
 * tomorrow's: at 07:00 with a 04:00–06:00 window today's session is over, so
 * this returns 21 hours, not a negative number.
 */
export function minutesUntilWindowOpen(minuteOfDay, window) {
    const diff = hhmmToMinutes(window.earliestStart) - minuteOfDay;
    return diff >= 0 ? diff : diff + 24 * 60;
}
/**
 * The session to act on at a local instant, and whether its window is open.
 *
 * @param localDate    local calendar date, YYYY-MM-DD
 * @param minuteOfDay  minutes since local midnight (0–1439)
 */
export function sessionPhase(localDate, minuteOfDay, window) {
    const startMin = hhmmToMinutes(window.earliestStart);
    const endMin = hhmmToMinutes(window.latestEnd);
    if (!windowWrapsMidnight(window)) {
        // Normal same-day window [start, end).
        if (minuteOfDay < endMin) {
            return { sessionDate: localDate, open: minuteOfDay >= startMin };
        }
        // Today's window has closed; the next session opens tomorrow.
        return { sessionDate: shiftDate(localDate, 1), open: false };
    }
    // Wrapping window: [start, midnight) ∪ [midnight, end).
    if (minuteOfDay >= startMin) {
        // Evening leg: this session opened today.
        return { sessionDate: localDate, open: true };
    }
    if (minuteOfDay < endMin) {
        // Early-morning leg: still inside the session that opened yesterday.
        return { sessionDate: shiftDate(localDate, -1), open: true };
    }
    // Daytime gap between sessions: tonight's opens later today.
    return { sessionDate: localDate, open: false };
}
//# sourceMappingURL=session.js.map